import { useCallback, useEffect, useMemo, useState } from 'react'
import { ArrowLeft } from 'lucide-react'
import { useSearchParams } from 'react-router-dom'
import {
  useGetPipeline,
  useGetSelectedApplication,
  useGetInterviews,
} from '../hooks/backend/interviews'
import { useInterviewDecisions } from '../hooks/useInterviewDecisions'
import { useManager } from '../hooks/useManager'
import { useAccess } from '../hooks/useAccess'
import { useCapabilities } from '../hooks/useCapabilities'
import { cn } from '../lib/shadcn/utils'
import { PipelineList } from '../components/interviews/PipelineList'
import { ApplicationDetail } from '../components/interviews/ApplicationDetail'
import { ReferenceRuleReportButton } from '../components/interviews/ReferenceRuleReport'
import type { OutcomeAction } from '../components/interviews/DecisionActions'
import type {
  ApplicationRow,
  SelectedApplication,
  InterviewRow,
} from '../utils/interviews'

type TabKey = 'interview' | 'offer' | 'active' | 'rejected'

const TABS: { key: TabKey; label: string }[] = [
  { key: 'interview', label: 'Interview' },
  { key: 'offer', label: 'Offer' },
  { key: 'active', label: 'Active' },
  { key: 'rejected', label: 'Rejected' },
]

function isTabKey(v: string | null): v is TabKey {
  return v === 'interview' || v === 'offer' || v === 'active' || v === 'rejected'
}

// Each tab maps to the set of pipeline statuses shown in the list.
const TAB_STATUSES: Record<TabKey, string[]> = {
  // 'submitted' is an application still being read by the document reader. It
  // sits with Interview, where it will land if it passes, so it is never on no tab.
  interview: [
    'submitted',
    'interview_1_pending',
    'interview_1_booked',
    'interview_2_pending',
    'interview_2_booked',
  ],
  offer: [
    'offer_pending',
    'offer_accepted',
    'onboarding_form_pending',
    'contract_pending',
    'contract_signed',
    'induction_pending',
    'induction_acknowledged',
    'ready_to_activate',
  ],
  active: ['active', 'offboarding', 'compliance', 'suspended', 'dismissed', 'resigned'],
  // Every application that was closed, by the system or by a manager, kept so
  // anybody can be found and the reason read. 24 September 2026.
  rejected: ['rejected_score', 'interview_1_failed', 'interview_2_failed', 'interview_1_no_show', 'withdrawn'],
}

export default function DriverPipeline() {
  const { linked, loading: managerLoading } = useManager()
  const { canAct, isViewer, caption } = useAccess()
  const { canEditDrivers } = useCapabilities()
  const [search, setSearch] = useState('')
  // The search box in the top bar opens an applicant here with ?tab= and ?app=.
  const [params] = useSearchParams()
  const tabParam = params.get('tab')
  const appParam = params.get('app')
  const [activeTab, setActiveTab] = useState<TabKey>(isTabKey(tabParam) ? tabParam : 'interview')
  const [selectedId, setSelectedId] = useState<string | null>(appParam)

  useEffect(() => {
    if (isTabKey(tabParam)) setActiveTab(tabParam)
    if (appParam) setSelectedId(appParam)
  }, [tabParam, appParam])

  const pipeline = useGetPipeline()
  const selApp = useGetSelectedApplication()
  const interviews = useGetInterviews()
  const { submit, busyId } = useInterviewDecisions()

  const pipelineTrigger = pipeline.trigger
  const selTrigger = selApp.trigger
  const ivTrigger = interviews.trigger

  const loadPipeline = useCallback(() => {
    pipelineTrigger({ statuses: TAB_STATUSES[activeTab] }, { skipCache: true })
  }, [pipelineTrigger, activeTab])

  useEffect(() => {
    loadPipeline()
  }, [loadPipeline])

  const loadSelected = useCallback(
    (id: string) => {
      selTrigger({ applicationId: id }, { skipCache: true })
      ivTrigger({ applicationId: id }, { skipCache: true })
    },
    [selTrigger, ivTrigger],
  )

  useEffect(() => {
    if (selectedId) loadSelected(selectedId)
  }, [selectedId, loadSelected])

  const allApplications = (pipeline.data as ApplicationRow[] | undefined) ?? []

  // Text search over name / email / phone, applied client-side.
  const applications = useMemo(() => {
    const term = search.trim().toLowerCase()
    if (!term) return allApplications
    return allApplications.filter((a) => {
      const hay = [a.full_name, a.primary_email, a.primary_phone]
        .filter(Boolean)
        .join(' ')
        .toLowerCase()
      return hay.includes(term)
    })
  }, [allApplications, search])

  const selectedApplication = (selApp.data as SelectedApplication | null | undefined) ?? null
  const interviewRows = (interviews.data as InterviewRow[] | undefined) ?? []

  // Viewers are read-only; managers/admins can act once linked to a person.
  const detailCanAct = canAct && linked

  const handleSubmit = useCallback(
    async (interviewId: string, action: OutcomeAction, notes: string | null) => {
      await submit(interviewId, action, notes, () => {
        if (selectedId) loadSelected(selectedId)
        loadPipeline()
      })
    },
    [submit, selectedId, loadSelected, loadPipeline],
  )

  return (
    <div className="flex h-full min-h-0 flex-col lg:grid lg:grid-cols-[380px_1fr]">
      {/* List — hidden on mobile once an applicant is selected. */}
      <aside
        className={cn(
          'min-h-0 flex-1 flex-col border-b border-border bg-card lg:flex lg:flex-none lg:border-b-0 lg:border-r',
          selectedId ? 'hidden lg:flex' : 'flex',
        )}
      >
        <div className="flex flex-wrap items-center justify-between gap-2 border-b border-border px-3 py-2 text-xs text-muted-foreground lg:px-4">
          <span>{caption}</span>
          {/* The 90 day review of the conditional second reference (2803). */}
          <ReferenceRuleReportButton />
        </div>

        {/* Segmented tabs drive which statuses the list shows. */}
        <div className="border-b border-border p-3">
          <div className="grid grid-cols-4 gap-1 rounded-md bg-muted p-1">
            {TABS.map((t) => (
              <button
                key={t.key}
                type="button"
                onClick={() => setActiveTab(t.key)}
                className={cn(
                  'rounded-sm px-3 py-1.5 text-sm font-medium transition-colors',
                  activeTab === t.key
                    ? 'bg-background text-foreground shadow-retool-sm'
                    : 'text-muted-foreground hover:text-foreground',
                )}
              >
                {t.label}
              </button>
            ))}
          </div>
        </div>

        <PipelineList
          applications={applications}
          loading={pipeline.loading}
          error={pipeline.error}
          search={search}
          onSearchChange={setSearch}
          selectedStatuses={TAB_STATUSES[activeTab]}
          onStatusChange={() => {}}
          selectedId={selectedId}
          onSelect={setSelectedId}
          showStatusFilter={false}
        />
      </aside>

      {/* Detail — hidden on mobile until an applicant is selected. */}
      <main
        className={cn(
          'min-h-0 flex-1 flex-col bg-background lg:flex',
          selectedId ? 'flex' : 'hidden lg:flex',
        )}
      >
        {selectedId && (
          <button
            type="button"
            onClick={() => setSelectedId(null)}
            className="flex items-center gap-2 border-b border-border px-4 py-3 text-sm font-medium text-muted-foreground transition-colors hover:text-foreground lg:hidden"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to applicants
          </button>
        )}
        <div className="min-h-0 flex-1">
          <ApplicationDetail
            app={selectedApplication}
            appLoading={selApp.loading}
            appError={selApp.error}
            interviews={interviewRows}
            interviewsLoading={interviews.loading}
            interviewsError={interviews.error}
            canAct={detailCanAct}
            isViewer={isViewer}
            managerLinked={linked}
            managerLoading={managerLoading}
            busyInterviewId={busyId}
            onSubmit={handleSubmit}
            canEditDrivers={canEditDrivers}
            isActiveTab={activeTab === 'active'}
            onDriverSaved={() => {
              if (selectedId) loadSelected(selectedId)
              loadPipeline()
            }}
          />
        </div>
      </main>
    </div>
  )
}
